import {FC} from "react"

import {css, cx} from "@styled-system/css"

import {widgetChrome} from "./brandChrome"

type ProductTitleMarkProps = {
  className?: string
  /**
   * Text to mark. Defaults to `productTitle` (header / wordmark).
   * Pass greeting copy only when the sparkle must sit on the same
   * “Repair Advisor” **i** without changing chrome.productTitle.
   */
  title?: string
  /** Sparkle size over the last "i"; scales with title size. */
  starSize?: number
}

/**
 * Wordmark treatment: product title with the last "i" dotted by the
 * brand sparkle when the theme enables greeting-title sparkle.
 *
 * Always sparkles the last "i" in `title` so a greeting that starts with
 * "Hi" still marks the product name, not the greeting.
 */
export const ProductTitleMark: FC<ProductTitleMarkProps> = ({
  className,
  title = widgetChrome.productTitle,
  starSize = 15,
}) => {
  const iIndex = title.lastIndexOf("i")
  const starOffset = Math.round(starSize * 0.67)

  if (iIndex === -1) {
    return (
      <span className={className} style={{fontFamily: widgetChrome.fontFamily}}>
        {title}
      </span>
    )
  }

  const before = title.slice(0, iIndex)
  const after = title.slice(iIndex + 1)

  return (
    <span
      className={cx(
        css({
          display: "inline-block",
          color: "text.primary",
        }),
        className,
      )}
      style={{fontFamily: widgetChrome.fontFamily}}
    >
      {before}
      <span style={{position: "relative", display: "inline-block"}}>
        {/* Dotless "i" (U+0131) so the sparkle fully replaces the dot. */}ı
        <svg
          width={starSize}
          height={starSize}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "50%",
            top: -starOffset,
            transform: "translateX(-50%)",
          }}
        >
          <path
            d="M11.999 1.83203C12.7991 4.068 14.222 6.21072 15.9922 7.98828C17.7608 9.76426 19.8989 11.1971 22.1484 11.999C19.8988 12.8009 17.7608 14.2347 15.9922 16.0107C14.2219 17.7885 12.7992 19.932 11.999 22.168C11.1988 19.9323 9.77768 17.7892 8.00781 16.0117C6.23917 14.2355 4.10123 12.8011 1.85156 11.999C4.10107 11.1971 6.2393 9.76426 8.00781 7.98828C9.77771 6.21088 11.1988 4.06773 11.999 1.83203Z"
            fill={widgetChrome.starFill}
          />
        </svg>
      </span>
      {after}
    </span>
  )
}
